/* eslint-disable */
/**
 *
 * This file contains Alpine.js components and functions to manage the filter drawer,
 * apply filters, handle sorting, and implement infinite scroll for the product listing.
 *
 * Key Features:
 * - Toggle and manage the filter drawer state
 * - Apply filters and sorting to dynamically update the product grid
 * - Infinite scrolling to load more products when the user reaches the bottom of the page
 * - Handle URL parameters for sorting, pagination, and filters
 *
 */
document.addEventListener('alpine:init', () => {
  Alpine.store('filterDrawerOpen', false);

  Alpine.data('filterDrawer', ({ sectionId }) => ({
    activeFilters: new Map(),
    filterDrawerOpen: false,
    isCenterLoading: false,
    openDrawers: Alpine.$persist([]).using(sessionStorage),
    isLoading: false,

    init() {
      // Load active filters from URL on component initialization
      this.loadActiveFiltersFromUrl();

      // Listen for popstate event (browser back/forward) to reapply filters
      window.addEventListener('popstate', () => {
        this.loadActiveFiltersFromUrl();
        this.updateProductGrid(window.location.href);
      });

      // Intercept clicks on pagination links to handle them via Alpine.js
      // This is crucial for controlling the URL and maintaining filters
      document.querySelectorAll('nav[role="navigation"]:not(.breadcrumb-nav) a').forEach(link => {
        link.addEventListener('click', (event) => {
          event.preventDefault(); // Prevent default link behavior
          const href = event.currentTarget.href;
          const url = new URL(href);
          // Extract page number from the clicked URL
          const page = url.searchParams.get('page');

          // Rebuild URL with existing filters and new page, then update grid
          const newUrl = this._buildParamsUrl(page);
          window.history.replaceState(null, null, newUrl);
          this.updateProductGrid(newUrl);
        });
      });
    },

    /* Add to open drawers */
    addToDrawers(drawer) {
      if (this.openDrawers.includes(drawer)) {
        this.openDrawers = this.openDrawers.filter(item => item !== drawer);
      } else {
        this.openDrawers.push(drawer);
      }
    },

    /* Load the active filters from the URL query parameters */
    loadActiveFiltersFromUrl() {
      this.activeFilters.clear(); // Clear existing filters to ensure fresh state
      const urlParams = new URLSearchParams(window.location.search);
      for (const [key, value] of urlParams.entries()) {
        // Only include filter parameters and other relevant non-page/sort_by params
        if (key.startsWith('filter.') || (key !== 'sort_by' && key !== 'page')) {
          this.activeFilters.set(key, value);
        }
      }
    },

    /* Toggle the filter drawer on desktop view */
    toggleFilterDrawer(showText, hideText) {
      this.toggleDrawerState(showText, hideText);
      Alpine.store('filterDrawerOpen', this.filterDrawerOpen);
    },

    toggleMobileFilterDrawer(showText, hideText) {
      this.filterDrawerOpen = !this.filterDrawerOpen;
      Alpine.store('filterDrawerOpen', this.filterDrawerOpen);
      this.updateDrawerButtonText(showText, hideText);
      this.updateFilterDrawerClass();
      if (this.filterDrawerOpen) {
        this.updateProductCount();
      }
    },

    updateProductCount() {
      const productCountElement = document.querySelector('.total-products-count');
      if (productCountElement) {
        const productCount = productCountElement.innerText;
        this.updateMobileButtonText(productCount);
      }
    },

    /* Common function to toggle the drawer state and update the button text */
    toggleDrawerState(showText, hideText) {
      this.filterDrawerOpen = !this.filterDrawerOpen;
      this.updateDrawerButtonText(showText, hideText);
      this.updateFilterDrawerClass();
    },

    /* Update the text of the drawer button based on the drawer state */
    updateDrawerButtonText(showText, hideText) {
      const buttonRef = this.$refs.filterButton; // Assume this refers to the main filter button
      const mobileButtonRef = this.$refs.mobileFilterButton; // Assume this refers to the mobile filter button

      if (buttonRef) buttonRef.innerText = this.filterDrawerOpen ? hideText : showText;
    },

    /* Update the drawer class to toggle visibility and handle body overflow */
    updateFilterDrawerClass() {
      const drawer = document.querySelector('.filter-drawer');
      const body = document.body;
      const classes = {
        isHidden: 'is-filter-hidden',
        isOpen: 'is-filter-open',
      };

      if (this.filterDrawerOpen) {
        drawer.classList.remove(classes.isHidden);
        drawer.classList.add(classes.isOpen);
        body.classList.add(classes.isOpen);
        body.style.overflow = window.innerWidth < 1024 ? 'hidden' : '';
      } else {
        drawer.classList.add(classes.isHidden);
        drawer.classList.remove(classes.isOpen);
        body.classList.remove(classes.isOpen);
        body.style.overflow = 'auto';
      }
    },

    /* Apply sorting and update the URL with the sorting parameters and page number */
    applySorting(sortBy, page) {
      // Ensure active filters are loaded before building the URL
      this.loadActiveFiltersFromUrl();
      const newUrl = this._buildParamsUrl(page, sortBy); // Pass sortBy as well
      window.history.replaceState(null, null, newUrl);
      this.updateProductGrid(newUrl);
    },

    /* Apply filter and update the URL with the active filters and page number */
    applyFilter(page) {
      this.loadActiveFiltersFromUrl(); // Re-load filters from the form for immediate update
      const newUrl = this._buildParamsUrl();
      window.history.replaceState(null, null, newUrl);
      this.updateProductGrid(newUrl);
    },

    handleClear({ url, closeDrawers = false, clearFilters = false }) {
      window.history.replaceState(null, null, url);
      if (closeDrawers) this.openDrawers = [];
      if (clearFilters) this.activeFilters.clear();
      this.loadActiveFiltersFromUrl(); // Re-load active filters from the (now cleared) URL

      // Replaces DOM, must come last
      this.updateProductGrid(url);
    },

    /* Build URL with form and current params */
    _buildParamsUrl(page = false, sortBy = false) {
      const currentUrl = new URL(window.location.href);
      const searchParams = currentUrl.searchParams;
      const formDataParams = this.$refs.filter_form
        ? new URLSearchParams(new FormData(this.$refs.filter_form))
        : new URLSearchParams();
      const newParams = new URLSearchParams();

      // 1. Add page param
      if (page) {
        newParams.set('page', page.toString());
      }

      // 2. Add sort_by param
      if (sortBy) {
        newParams.set('sort_by', sortBy);
      } else if (searchParams.has('sort_by')) {
        newParams.set('sort_by', searchParams.get('sort_by'));
      }

      // 3. Add form params (active filter selections from the form)
      formDataParams.forEach((value, key) => {
        if (value.trim().length === 0) return;
        if (key.startsWith('filter.')) { // Ensure only filter params are added from form
          newParams.append(key, value);
        }
      });

      // 4. Add automatic availability filter if enabled and no user interaction with availability
      this._addAutoAvailabilityFilter(newParams, formDataParams);

      // 5. Add any other non-filter, non-page, non-sort_by params
      searchParams.forEach((value, key) => {
        if (!key.startsWith('filter.') && key !== 'sort_by' && key !== 'page') {
          newParams.append(key, value);
        }
      });

      return currentUrl.pathname + (newParams.toString() ? '?' + newParams.toString() : '');
    },

    // Add automatic availability filter if setting is enabled and user hasn't manually set it
    _addAutoAvailabilityFilter(newParams, formDataParams) {
      const AVAILABILITY_PARAM = 'filter.v.availability';
      const AVAILABILITY_VALUE = '1';

      // Check if auto filter is enabled
      const isAutoFilterEnabled = window.Shopify?.theme?.settings?.auto_filter_in_stock ||
                                   window.themeSettings?.auto_filter_in_stock ||
                                   window.autoFilterInStock === true;

      if (!isAutoFilterEnabled) {
        return;
      }

      // ! Check if user has manually interacted with availability filter
      const userHasSetAvailability = formDataParams.has(AVAILABILITY_PARAM);

      const availabilityCheckbox = document.querySelector(`input[name="${AVAILABILITY_PARAM}"]`);
      const userHasUncheckedAvailability = availabilityCheckbox && !availabilityCheckbox.checked;

      // Only add auto filter if user hasn't explicitly set it or unchecked it
      if (!userHasSetAvailability && !userHasUncheckedAvailability && !newParams.has(AVAILABILITY_PARAM)) {
        newParams.set(AVAILABILITY_PARAM, AVAILABILITY_VALUE);
      }
    },

    /* Fetch and update the product grid after applying filters or sorting */
    updateProductGrid(url) {
      this.isCenterLoading = true;
      fetch(url)
        .then(response => response.text())
        .then(html => this.replaceProductGrid(html))
        .catch(error => console.error('Error fetching updated products:', error))
        .finally(() => {
          this.isCenterLoading = false;
          const productlistsection = document.querySelector('#product_list');
          if (productlistsection) {
            const headerHeight = document.querySelector('header')?.offsetHeight || 0;
            const topposition = productlistsection.offsetTop - headerHeight;
            window.scrollTo({ top: topposition, behavior: 'smooth' });
          }else{
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
    },

    /* Replace the product grid and active filter section with new data */
    replaceProductGrid(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const productGridId = `product-grid-${sectionId}`;
      const productsEmptyId = `product-grid-${sectionId}-empty`;
      const classActiveFilters = '.active-filters';
      const classClearFilters = '.clear-filters';
      const classProductCount = '.product-count';
      const classTotalProductsCount = '.total-products-count'
      const paginationSelector = 'nav[role="navigation"]:not(.breadcrumb-nav)';
      const classUpdateFilters = '.update-filters';
      const newProductGrid = doc.getElementById(productGridId);
      const productsEmpty = doc.getElementById(productsEmptyId);
      const activeFiltersSection = doc.querySelector(classActiveFilters);
      const clearFiltersButton = doc.querySelector(classClearFilters);
      const productCount = doc.querySelector(classProductCount);
      const totalProductsCount = doc.querySelector(classTotalProductsCount);
      const updateFilters = doc.querySelector(classUpdateFilters);
      const newPagination = doc.querySelector(paginationSelector); // Get new pagination HTML
      const currentPagination = document.querySelector(paginationSelector); // Current pagination in the DOM

      if (newProductGrid) {
        document.getElementById(productGridId).innerHTML = newProductGrid.innerHTML;
      }

      if (productsEmpty && !newProductGrid) {
        document.getElementById(productGridId).innerHTML = productsEmpty.innerHTML;
      }

      if (activeFiltersSection) {
        document.querySelector(classActiveFilters).innerHTML = activeFiltersSection.innerHTML;
      }

      if (clearFiltersButton) {
        document.querySelector(classClearFilters).innerHTML = clearFiltersButton.innerHTML;
      }

      if (productCount) {
        document.querySelector(classProductCount).innerHTML = productCount.innerHTML;
      }

      if (productCount) {
        const currentCountElement = document.querySelector(classProductCount);
        currentCountElement.innerHTML = productCount.innerHTML;

        // ✅ Update the data-product-count attribute
        const template = currentCountElement.dataset.productCountTemplate;
        const countMatch = productCount.innerText.match(/\d+/);
        if (template && countMatch) {
          const newText = template.replace('__count__', countMatch[0]);
          currentCountElement.setAttribute('data-product-count', newText);
        }
    }

      if (totalProductsCount) {
        document.querySelector(classTotalProductsCount).innerHTML = totalProductsCount.innerHTML;
        this.updateMobileButtonText(totalProductsCount.innerHTML);
      }

      if (updateFilters) {
        document.querySelector(classUpdateFilters).innerHTML = updateFilters.innerHTML;
      }

      // Update pagination HTML
      if (newPagination && currentPagination) {
        currentPagination.classList.remove('hidden');
        currentPagination.innerHTML = newPagination.innerHTML;
      } else if (currentPagination) {
        currentPagination.classList.add('hidden');
      }

      // Important: Re-initialize filters after DOM update for internal Alpine.js state
      this.loadActiveFiltersFromUrl();

      // Re-attach event listeners to new pagination links
      document.querySelectorAll('nav[role="navigation"]:not(.breadcrumb-nav) a').forEach(link => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          const href = event.currentTarget.href;
          const url = new URL(href);
          const page = url.searchParams.get('page');
          const newUrl = this._buildParamsUrl(page); // Use _buildParamsUrl to include filters
          window.history.replaceState(null, null, newUrl);
          this.updateProductGrid(newUrl);
        });
      });

      // Infinite scroll logic might also need to reset page if this is a filter change,
      // but if coming from pagination, it should maintain.
      // The original code sets currentPage to 1, which might be incorrect for pagination clicks.
      // This part needs careful consideration based on your desired behavior for infinite scroll
      // when filters or direct pagination links are used.
      // if (this.$data.currentPage) this.$data.currentPage = 1; // Consider removing or adjusting
      // if (this.$data.isLastPage) this.$data.isLastPage = false; // Consider removing or adjusting
    },

    /* Update the text of the mobile button based on the product count */
    updateMobileButtonText(count) {
      const cleanCount = count.toString().replace(/[()]/g, '').trim();
      if (window.translations && window.translations.collection_grid.hide_filter_sorting_count) {
        let hideText = window.translations.collection_grid.hide_filter_sorting_count.replace('{{ count }}', cleanCount);

        const mobileButtonRef = this.$refs.mobileFilterButton;
        if (mobileButtonRef) mobileButtonRef.innerText = hideText;
      } else {
        console.error('Translations not found');
      }
    },
  }));

  /* Alpine.js component for infinite scrolling */
  Alpine.data('infiniteScroll', ({ currentPage, totalPages, sectionId }) => ({
    currentPage,
    totalPages,
    isLoading: false,
    productGrid: null,

    init() {
      this.productGrid = document.getElementById(`product-grid-${sectionId}`);
      if (this.currentPage > 1) this.loadPreviousPages();
      this.scrollToCurrentPage();
      window.addEventListener('scroll', this.handleScroll.bind(this));
    },

    get isLastPage() {
      return this.currentPage >= this.totalPages;
    },

    /* Load more products when the user scrolls near the bottom of the page */
    async loadMore() {
      if (this.isLoading || this.isLastPage || Alpine.store('filterDrawerOpen')) return;

      this.isLoading = true;
      const nextPage = this.currentPage + 1;

      try {
        const url = this.buildUrlWithPage(nextPage);
        const response = await fetch(url);
        const html = await response.text();

        this.appendNewProducts(html, sectionId);
        this.currentPage = nextPage;
        this.updatePage(this.currentPage);
      } catch (error) {
        console.error('Error loading more products:', error);
      } finally {
        this.isLoading = false;
      }
    },

    /* Build the URL for the next page (for infinite scroll) */
    buildUrlWithPage(page) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('page', page.toString());
      // Ensure all active filters are also included in the URL for infinite scroll
      const filterDrawerData = Alpine.raw(Alpine.data('filterDrawer'));
      if (filterDrawerData && filterDrawerData.activeFilters) {
        filterDrawerData.activeFilters.forEach((value, key) => {
          if (key.startsWith('filter.')) { // Only include filter params
            currentUrl.searchParams.set(key, value);
          }
        });
      }
      return currentUrl.toString();
    },

    /* Append the new products to the existing product grid */
    appendNewProducts(html, sectionId) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newProducts = doc.getElementById(`product-grid-${sectionId}`);

      if (newProducts) this.productGrid.insertAdjacentHTML('beforeend', newProducts.innerHTML);
    },

    /* Load the previous pages when necessary */
    async loadPreviousPages() {
      const pages = Array.from({ length: this.currentPage - 1 }, (_, i) => i + 1);
      this.isLoading = true;

      try {
        const responses = await Promise.all(pages.map(page => this.fetchPageHtml(page)));
        const previousProducts = this.extractProductsFromPages(responses, sectionId);
        this.prependPreviousPagesProducts(previousProducts);
      } catch (error) {
        console.error('Error loading previous pages:', error);
      } finally {
        this.isLoading = false;
      }
    },

    /* Fetch the HTML content of a specific page */
    async fetchPageHtml(page) {
      const pageUrl = new URL(window.location.href);
      pageUrl.searchParams.set('page', page.toString());
      // Ensure active filters are passed when fetching previous pages
      const filterDrawerData = Alpine.raw(Alpine.data('filterDrawer'));
      if (filterDrawerData && filterDrawerData.activeFilters) {
        filterDrawerData.activeFilters.forEach((value, key) => {
          if (key.startsWith('filter.')) { // Only include filter params
            pageUrl.searchParams.set(key, value);
          }
        });
      }
      return fetch(pageUrl.toString()).then(response => response.text());
    },

    /* Extract the product grid from the response HTML */
    extractProductsFromPages(responses, sectionId) {
      return responses
        .map(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          return doc.getElementById(`product-grid-${sectionId}`).innerHTML;
        })
        .join('');
    },

    /* Prepend previous pages' products to the product grid */
    prependPreviousPagesProducts(previousProducts) {
      this.productGrid.insertAdjacentHTML('afterbegin', previousProducts);
    },

    /* Scroll to the current page based on the URL */
    scrollToCurrentPage() {
      const urlParams = new URLSearchParams(window.location.search);
      const page = parseInt(urlParams.get('page')) || 1;
      const pageHeight = this.productGrid.clientHeight;

      // Calculate the scroll position based on the current page
      const scrollPosition = (page - 1) * pageHeight;
      window.scrollTo({
        top: scrollPosition,
      });
    },

    /* Update the page number in the browser's URL */
    updatePage(page) {
      const currentUrl = new URL(window.location.href);
      const searchParams = currentUrl.searchParams;

      // Conditionally update page parameter
      page === 1
        ? searchParams.delete('page')
        : searchParams.set('page', page.toString());

      window.history.replaceState({}, '', currentUrl.toString());
    },

    /* Handle scroll event to load more products when near the bottom of the page */
    handleScroll() {
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 500) {
        Alpine.store('filterDrawerOpen', false);
        this.loadMore();
      }
    },
  }));
});
/* eslint-enable */
